import { Link } from '@tanstack/react-router';
import { Plus, SquareStack, Store } from 'lucide-react';
import type { Desk, DeskPulseState } from '@deskworks/shared-types';
import { DeskCard, NewDeskCard } from '@/components/desk-card';
import { rangeLabel, useActivityRange } from '@/components/desk/activity';
import { PULSE } from '@/components/desk/pulse';
import { Badge, dotTone } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { CreateDeskDialog } from '@/components/desk-dialogs';
import { Page, PageHeader } from '@/components/page';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { useDesks, useWorkspaceId } from '@/lib/hooks';
import { inSentence, t, tr } from '@/lib/i18n';

export function DesksPage() {
  const workspaceId = useWorkspaceId();
  const desks = useDesks(workspaceId);

  return (
    <Page>
      <PageHeader
        title={t('Desks')}
        description={t('A desk is an area of responsibility that Deskworks takes care of for you. Every desk has a Lead Agent that decides what needs to be done.')}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/w/$workspaceId/desk-store" params={{ workspaceId }}>
                <Store /> Desk Store
              </Link>
            </Button>
            <CreateDeskDialog workspaceId={workspaceId}>
              <Button>
                <Plus /> {t('Create desk')}
              </Button>
            </CreateDeskDialog>
          </>
        }
      />
      {desks.isPending ? (
        <LoadingRows rows={4} />
      ) : desks.isError ? (
        <ErrorState error={desks.error} onRetry={() => void desks.refetch()} />
      ) : desks.data.length === 0 ? (
        <EmptyState
          icon={<SquareStack />}
          title={t('No desks yet')}
          description={t('Install a ready-made desk from the Desk Store, or create your own — for example for support requests, daily reports or research.')}
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button asChild>
                <Link to="/w/$workspaceId/desk-store" params={{ workspaceId }}>
                  <Store /> {t('Open the Desk Store')}
                </Link>
              </Button>
              <CreateDeskDialog workspaceId={workspaceId}>
                <Button variant="outline">
                  <Plus /> {t('Create your first desk')}
                </Button>
              </CreateDeskDialog>
            </div>
          }
        />
      ) : (
        <>
          <PulseSummary desks={desks.data} />
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {desks.data.map((d) => (
              <DeskCard key={d.id} desk={d} />
            ))}
            <NewDeskCard workspaceId={workspaceId} />
          </div>
        </>
      )}
    </Page>
  );
}

const SUMMARY_ORDER: DeskPulseState[] = ['needs_you', 'working', 'checking', 'watching', 'paused'];

/**
 * "2 working · 1 needs you": how the workspace's desks are doing, as on the landing page; how many of their agents
 * run now; then the Work Items in progress, open now and finished within the cards' window.
 */
function PulseSummary({ desks }: { desks: Desk[] }) {
  const [range] = useActivityRange();
  const sum = (f: (w: NonNullable<Desk['pulse']>['workItems']) => number) =>
    desks.reduce((n, d) => n + (d.pulse?.workItems ? f(d.pulse.workItems) : 0), 0);
  const active = sum((w) => w.active);
  const running = sum((w) => w.running);
  const done = sum((w) => w.done[range]);
  const failed = sum((w) => w.failed[range]);
  const counts = new Map<DeskPulseState, number>();
  for (const d of desks) if (d.pulse) counts.set(d.pulse.state, (counts.get(d.pulse.state) ?? 0) + 1);
  const agents = new Set(desks.flatMap((d) => d.pulse?.team.map((a) => a.agentId) ?? [])).size;
  const working = new Set(desks.flatMap((d) => d.pulse?.team.filter((a) => a.working).map((a) => a.agentId) ?? [])).size;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
      <Badge tone="green">
        <span className={cn('size-1.5 rounded-full animate-soft-pulse', dotTone.green)} />
        {desks.length === 1 ? t('1 desk') : t('{n} desks', { n: desks.length })}
      </Badge>
      {SUMMARY_ORDER.filter((s) => counts.get(s)).map((s) => (
        <Badge key={s} tone={PULSE[s].tone}>
          <span className={cn('size-1.5 rounded-full', dotTone[PULSE[s].tone], PULSE[s].live && 'animate-soft-pulse')} />
          {counts.get(s)} {inSentence(PULSE[s].label)}
        </Badge>
      ))}
      {agents > 0 && (
        <span className="text-muted-foreground tabular-nums">
          {t('{n} agents on duty', { n: agents })}
          {working > 0 && <> · {tr('{n} working now', { n: <span className="font-medium text-foreground">{working}</span> })}</>}
        </span>
      )}
      <span className="text-muted-foreground tabular-nums sm:ml-auto">
        {tr('{n} in progress', { n: <span className="font-medium text-foreground">{running}</span> })} ·{' '}
        {active === 1
          ? tr('{n} open work item', { n: <span className="font-medium text-foreground">{active}</span> })
          : tr('{n} open work items', { n: <span className="font-medium text-foreground">{active}</span> })}{' '}
        ·{' '}
        {tr('{n} done', { n: <span className="font-medium text-foreground">{done}</span> })}
        {failed > 0 && (
          <>
            {' '}
            · {tr('{n} failed', { n: <span className="font-medium text-red-600 dark:text-red-400">{failed}</span> })}
          </>
        )}{' '}
        · {rangeLabel(range)}
      </span>
    </div>
  );
}
